import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Alert,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Badge,
  Stack
} from '@mui/material';
import { 
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CallMade as CallMadeIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { api } from '../../../../services/api';

// Import styled components
import {
  DetailSection,
  DetailContainer
} from '../../TripDetailPanel.styles';
import {
  LoadingContainer,
  StatusChip,
  IconContainer
} from '../../styles/Common.styles';
import styles from './CallbackRequestPanel.module.css';

interface CallbackRequest {
  id: number;
  customer: any | null;
  mobileNumber: string;
  status: string;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  completedAt?: string | null;
  completedBy?: string | null;
}

interface CallbackResponse {
  status: boolean;
  message: string;
  data: CallbackRequest[];
}

interface CallbackRequestPanelProps {
  // No props needed - component fetches its own data
}

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  'PENDING': 'warning',
  'IN_PROGRESS': 'info',
  'COMPLETED': 'success',
  'CANCELLED': 'error',
  'CALLED_BACK': 'success'
};

const priorityColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  'HIGH': 'error',
  'MEDIUM': 'warning',
  'LOW': 'info'
};

const CallbackRequestPanel: React.FC<CallbackRequestPanelProps> = () => {
  const [callbacks, setCallbacks] = useState<CallbackRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCallback, setSelectedCallback] = useState<CallbackRequest | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateNotes, setUpdateNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const hasFetched = useRef(false);

  const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'CALLED_BACK', label: 'Called Back' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  // Fetch callback data when component mounts
  const fetchCallbacks = async () => {
    try {
      setLoading(!refreshing);
      setError(null);
      
      const response = await api.getPendingCallbacks() as CallbackResponse | CallbackRequest[];
      
      // Handle the actual API response structure
      let callbackData: CallbackRequest[] = [];
      if (response && typeof response === 'object' && 'status' in response && Array.isArray(response.data)) {
        callbackData = response.data;
      } else if (Array.isArray(response)) {
        callbackData = response;
      }
      
      setCallbacks(callbackData);
    } catch (err) {
      console.error('Error fetching callback requests:', err);
      setError('Failed to load callback requests');
      setCallbacks([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCallbacks();
    }
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCallbacks();
  };

  const handleUpdateCallback = (callback: CallbackRequest) => {
    setSelectedCallback(callback);
    setUpdateStatus(callback.status);
    setUpdateNotes(callback.notes || '');
    setUpdateDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedCallback) return;

    try {
      setUpdating(true);
      setError(null);
      
      const response = await api.updateCallbackStatus(selectedCallback.id, updateStatus, updateNotes);      
      // Update local state
      setCallbacks(prev => prev.map(callback => 
        callback.id === selectedCallback.id 
          ? { ...callback, status: updateStatus, notes: updateNotes }
          : callback
      ));
      
      setUpdateDialogOpen(false);
      setSelectedCallback(null);
      setSuccessMessage('Callback status updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err: any) {
      console.error('Error updating callback status:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update callback status';
      setError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'CALLED_BACK':
        return <CheckIcon fontSize="small" />;
      case 'CANCELLED':
        return <CancelIcon fontSize="small" />;
      case 'IN_PROGRESS':
        return <CallMadeIcon fontSize="small" />;
      default:
        return <TimeIcon fontSize="small" />;
    }
  };

  if (loading && !refreshing) {
    return (
      <DetailContainer>
        <DetailSection>
          <div className={styles.loadingContainer}>
            <CircularProgress size={40} />
            <Typography variant="body1" className={styles.loadingText}>
              Loading callback requests...
            </Typography>
          </div>
        </DetailSection>
      </DetailContainer>
    );
  }

  return (
    <DetailContainer>
      <DetailSection className={styles.detailSection}>
        {/* Header */}
        <div className={styles.headerContainer}>
          <div className={styles.titleContainer}>
            <Typography variant="h4" gutterBottom className={styles.title}>
              Callback Requests
            </Typography>
            <Typography variant="subtitle1" className={styles.subtitle}>
              Manage customer callback requests efficiently
            </Typography>
          </div>
          <div className={styles.actionsContainer}>
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                className={styles.refreshButton}
              >
                {refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <div className={styles.badgeContainer}>
              <Badge 
                badgeContent={callbacks.filter(c => c.status === 'PENDING').length} 
                color="error"
                overlap="rectangular"
                className={styles.badge}
              >
                <Chip 
                  label={`${callbacks.length} Total`} 
                  color="primary" 
                  variant="outlined"
                  size="medium"
                  className={styles.totalChip}
                />
              </Badge>
            </div>
          </div>
        </div>

        {error && (
          <Alert severity="error" className={styles.alertContainer} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" className={styles.alertContainer} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {callbacks.length === 0 ? (
          <Paper className={styles.emptyStateContainer}>
            <PersonAddIcon className={styles.emptyStateIcon} />
            <Typography variant="h6" className={styles.emptyStateTitle}>
              No callback requests found
            </Typography>
            <Typography variant="body2" className={styles.emptyStateSubtitle}>
              Customer callback requests will appear here when submitted
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} className={styles.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow className={styles.tableHeader}>
                  {/* <TableCell className={styles.tableHeaderCell}>Customer</TableCell> */}
                  <TableCell className={styles.tableHeaderCell}>Mobile Number</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Status</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Notes</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Requested At</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {callbacks.map((callback) => (
                  <TableRow 
                    key={callback.id} 
                    hover
                    className={`${styles.tableRow} ${
                      ['COMPLETED', 'CANCELLED', 'CALLED_BACK'].includes(callback.status) 
                        ? styles.tableRowCompleted 
                        : ''
                    }`}
                  >
                    {/* <TableCell> */}
                      {/* <Typography variant="body1" className={styles.customerName}>
                        {callback.customer?.name || callback.customer?.customerName || 'Unknown Customer'}
                      </Typography> */}
                    {/* </TableCell> */}
                    <TableCell>
                      <div className={styles.iconContainer}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {callback.mobileNumber || 'N/A'}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusChip 
                        icon={getStatusIcon(callback.status)}
                        label={callback.status || 'PENDING'} 
                        color={statusColors[callback.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {callback.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <div className={styles.dateContainer}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(callback.createdAt)}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.actionsStack}>
                        <Tooltip title="Update Status">
                          <IconButton 
                            size="small" 
                            onClick={() => handleUpdateCallback(callback)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Call Customer">
                          <IconButton 
                            size="small" 
                            href={`tel:${callback.mobileNumber}`}
                            color="success"
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Update Status Dialog */}
        <div className={styles.dialogContainer}>
          <Dialog 
            open={updateDialogOpen} 
            onClose={() => setUpdateDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: '#2a2a2a',
                color: 'white'
              }
            }}
          >
            <DialogTitle sx={{ color: 'white' }}>
              Update Callback Status
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: '#2a2a2a', color: 'white' }}>
              <div className={styles.dialogContent}>
                <Typography variant="subtitle1" className={styles.customerInfo}>
                  Customer: {selectedCallback?.customer?.name || selectedCallback?.customer?.customerName || 'Unknown Customer'}
                </Typography>
                <Typography variant="body2" className={styles.mobileInfo}>
                  Mobile: {selectedCallback?.mobileNumber}
                </Typography>
                
                <FormControl fullWidth className={styles.statusFormControl}>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
                  <Select
                    value={updateStatus}
                    onChange={(e) => setUpdateStatus(e.target.value)}
                    label="Status"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6'
                      }
                    }}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  className={styles.notesField}
                  multiline
                  rows={3}
                  label="Notes (Optional)"
                  value={updateNotes}
                  onChange={(e) => setUpdateNotes(e.target.value)}
                  placeholder="Add any notes about this callback..."
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    }
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: '#2a2a2a' }}>
              <Button onClick={() => setUpdateDialogOpen(false)} sx={{ color: 'white' }}>
                Cancel
              </Button>
              <Button 
                onClick={handleStatusUpdate} 
                variant="contained"
                disabled={updating || !updateStatus}
                sx={{ backgroundColor: '#3b82f6' }}
              >
                {updating ? <CircularProgress size={20} /> : 'Update'}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </DetailSection>
    </DetailContainer>
  );
};

export default CallbackRequestPanel;
